import { useState, useEffect, useCallback } from 'react';

interface UseExitIntentOptions {
  enabled?: boolean;
  delay?: number;
  sensitivity?: number;
  storageKey?: string;
  sessionOnly?: boolean;
}

export const useExitIntent = (options: UseExitIntentOptions = {}) => {
  const {
    enabled = true,
    delay = 1000, // Mindestzeit auf der Seite bevor Exit-Intent aktiviert wird
    sensitivity = 20, // Pixel vom oberen Rand
    storageKey = 'exitIntentShown',
    sessionOnly = true // Nur einmal pro Session oder pro Tag
  } = options;

  const [showExitIntent, setShowExitIntent] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  // Prüfen ob bereits angezeigt wurde
  const checkIfAlreadyShown = useCallback(() => {
    try {
      const storage = sessionOnly ? sessionStorage : localStorage;
      const lastShown = storage.getItem(storageKey);
      
      if (sessionOnly) {
        return !!lastShown;
      } else {
        // Für localStorage: Prüfen ob heute bereits angezeigt
        if (lastShown) {
          const lastShownDate = new Date(lastShown);
          const today = new Date();
          return lastShownDate.toDateString() === today.toDateString();
        }
      }
      return false;
    } catch {
      return false;
    }
  }, [storageKey, sessionOnly]);

  // Markieren als angezeigt
  const markAsShown = useCallback(() => {
    try {
      const storage = sessionOnly ? sessionStorage : localStorage;
      const value = sessionOnly ? 'true' : new Date().toISOString();
      storage.setItem(storageKey, value);
    } catch {
      // Ignoriere Storage-Fehler
    }
  }, [storageKey, sessionOnly]);

  // Exit-Intent durch Mausbewegung nach oben erkennen
  const handleMouseLeave = useCallback((e: MouseEvent) => {
    if (!isEnabled || hasShown) return;
    
    // Prüfen ob Maus den oberen Bereich verlässt
    if (e.clientY <= sensitivity) {
      setShowExitIntent(true);
      setHasShown(true);
      markAsShown();
    }
  }, [isEnabled, hasShown, sensitivity, markAsShown]);

  // Visibility API - erkennt Tab-Wechsel
  const handleVisibilityChange = useCallback(() => {
    if (!isEnabled || hasShown) return;
    
    if (document.hidden) {
      setShowExitIntent(true);
      setHasShown(true);
      markAsShown();
    }
  }, [isEnabled, hasShown, markAsShown]);

  useEffect(() => {
    if (!enabled) return;

    // Prüfen ob bereits angezeigt wurde
    if (checkIfAlreadyShown()) {
      setHasShown(true);
      return;
    }

    // Delay bevor Exit-Intent aktiviert wird
    const timer = setTimeout(() => {
      setIsEnabled(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [enabled, delay, checkIfAlreadyShown]);

  useEffect(() => {
    if (!isEnabled) return;

    // Event Listeners hinzufügen (ohne beforeunload)
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Event Listeners entfernen
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isEnabled, handleMouseLeave, handleVisibilityChange]);

  const hideExitIntent = useCallback(() => {
    setShowExitIntent(false);
  }, []);

  const resetExitIntent = useCallback(() => {
    setShowExitIntent(false);
    setHasShown(false);
    try {
      const storage = sessionOnly ? sessionStorage : localStorage;
      storage.removeItem(storageKey);
    } catch {
      // Ignoriere Storage-Fehler
    }
  }, [storageKey, sessionOnly]);

  return {
    showExitIntent,
    hideExitIntent,
    resetExitIntent,
    hasShown
  };
}; 